import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Download, Printer, SpinnerGap, WarningCircle } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { toast } from "sonner";

const SCALE_OPTIONS = [
    { value: "0.9", label: "Kecil" },
    { value: "1.0", label: "Normal" },
    { value: "1.1", label: "Besar" },
    { value: "1.2", label: "Sangat Besar" },
];

export default function KartuPreview() {
    const { source, id } = useParams();
    const [searchParams] = useSearchParams();
    const kind = searchParams.get("kind") || "student";
    const [scale, setScale] = useState("1.0");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const isAdmin = source === "admin";
    const cardUrl = isAdmin
        ? `${API}/admin/athlete-card/${id}?kind=${kind}&scale=${scale}`
        : `${API}/parent/children/${id}/card?scale=${scale}`;
    const backTarget = isAdmin ? "/admin" : "/portal";

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        setLoading(true);
        setError(false);
    }, [cardUrl]);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const res = await fetch(cardUrl, { credentials: "include" });
            if (!res.ok) throw new Error("Gagal mengunduh kartu");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = `kartu-atlet-${Date.now()}.png`;
            anchor.click();
            URL.revokeObjectURL(url);
            toast.success("Kartu atlet berhasil diunduh");
        } catch (err) {
            toast.error("Gagal mengunduh kartu. Coba lagi.");
        } finally {
            setDownloading(false);
        }
    };

    const handlePrint = () => {
        const win = window.open("", "_blank");
        if (!win) {
            toast.error("Pop-up diblokir. Izinkan pop-up untuk mencetak.");
            return;
        }
        win.document.write(`<html><head><title>Kartu Atlet</title><style>@page { size: 90mm 55mm; margin: 0; } body { margin: 0; } img { width: 90mm; height: 55mm; display: block; }</style></head><body><img src="${cardUrl}" onload="setTimeout(function(){ window.print(); }, 250);" /></body></html>`);
        win.document.close();
    };

    return (
        <div className="min-h-screen bg-[#FBF9F5] text-neutral-900" data-testid="kartu-preview-page">
            <header className="border-b border-racing-line bg-white">
                <div className="max-w-6xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between gap-4">
                    <Link
                        to={backTarget}
                        data-testid="kartu-preview-back"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-racing-muted hover:text-racing-red transition-colors"
                    >
                        <ArrowLeft size={16} weight="bold" /> Kembali
                    </Link>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-racing-muted">Ukuran cetak: 90 × 55 mm</p>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 md:px-10 py-10">
                <h1 className="font-display uppercase text-4xl sm:text-5xl leading-none" data-testid="kartu-preview-title">Preview Kartu Atlet</h1>
                <p className="mt-3 text-sm font-light text-racing-muted max-w-2xl">
                    Pilih ukuran tulisan di bawah kartu — preview akan langsung berubah otomatis. Jika sudah pas, unduh PNG atau cetak langsung.
                </p>

                <div className="mt-8 border border-racing-line bg-white p-4 md:p-6" data-testid="kartu-preview-frame">
                    <div className="relative">
                        {loading && !error && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10" data-testid="kartu-preview-loading">
                                <SpinnerGap size={36} className="animate-spin text-racing-red" />
                            </div>
                        )}
                        {error ? (
                            <div className="flex flex-col items-center gap-3 py-20 text-center" data-testid="kartu-preview-error">
                                <WarningCircle size={40} className="text-racing-red" />
                                <p className="text-sm text-racing-muted">Kartu tidak dapat dimuat. Pastikan Anda sudah login lalu coba lagi.</p>
                            </div>
                        ) : (
                            <img
                                key={cardUrl}
                                src={cardUrl}
                                alt="Preview Kartu Atlet"
                                onLoad={() => setLoading(false)}
                                onError={() => { setLoading(false); setError(true); }}
                                data-testid="kartu-preview-image"
                                className="w-full h-auto border border-racing-line"
                            />
                        )}
                    </div>

                    <div className="mt-6 border-t border-racing-line pt-6">
                        <p className="text-[10px] uppercase tracking-[0.25em] text-racing-muted mb-3">Ukuran Tulisan Kartu</p>
                        <div className="flex flex-wrap gap-2" data-testid="kartu-preview-scale-options">
                            {SCALE_OPTIONS.map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => setScale(opt.value)}
                                    data-testid={`kartu-preview-scale-${opt.value.replace(".", "-")}`}
                                    className={`px-5 py-3 text-xs uppercase tracking-[0.15em] border transition-colors duration-300 ${
                                        scale === opt.value
                                            ? "bg-racing-red border-racing-red text-white"
                                            : "bg-white border-racing-line text-racing-muted hover:border-racing-red hover:text-racing-red"
                                    }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="mt-6 flex flex-wrap gap-3">
                        <button
                            type="button"
                            onClick={handleDownload}
                            disabled={downloading || error}
                            data-testid="kartu-preview-download-button"
                            className="inline-flex items-center gap-2 bg-racing-red hover:bg-racing-redhover disabled:opacity-60 text-white text-xs uppercase tracking-[0.2em] px-6 py-3.5 transition-colors duration-300"
                        >
                            {downloading ? <SpinnerGap size={16} className="animate-spin" /> : <Download size={16} weight="bold" />} Unduh PNG
                        </button>
                        <button
                            type="button"
                            onClick={handlePrint}
                            disabled={error}
                            data-testid="kartu-preview-print-button"
                            className="inline-flex items-center gap-2 border border-racing-gold text-racing-golddeep hover:bg-racing-gold/10 text-xs uppercase tracking-[0.2em] px-6 py-3.5 transition-colors duration-300"
                        >
                            <Printer size={16} weight="bold" /> Cetak (90 × 55 mm)
                        </button>
                    </div>
                </div>
            </main>
        </div>
    );
}
